/**
 * Figure spec registry — the only visual contract quizzes (and later PPTX,
 * worksheets and OG images) are allowed to carry.
 *
 * The generator never emits pixels or markup; it emits one of these specs and
 * our components do all the drawing. `kind` is an allow-list: an unknown kind
 * fails validation, so a new figure type cannot reach a learner until it has a
 * component, a prompt snippet and a schema variant.
 *
 * SERVER MIRROR: `server/validators/figure_spec.js` must stay in step with this
 * file. `src/tests/figures/figure-spec.test.ts` runs the same fixtures
 * through both and fails when they diverge.
 */
import { z } from 'zod';
import { decodeQuizHtmlEntities } from '../utils/quiz-text-utils';
import { MAX_EXPRESSION_LENGTH, isSupportedExpression } from './expression';
/** Registry allow-list. Adding a kind here without a component renders nothing. */
export const FIGURE_KINDS = ['cartesian_plot', 'number_line'];
/** Bump when the drawing output changes; Path B cache keys include it. */
export const FIGURE_SCHEMA_VERSION = 1;
/** Ceilings that keep a hostile or careless spec from melting a render. */
export const FIGURE_LIMITS = {
    maxSeries: 4,
    maxSeriesPoints: 400,
    maxMarkers: 24,
    maxIntervals: 6,
    maxTicksPerAxis: 40,
    maxExpressionLength: MAX_EXPRESSION_LENGTH,
};
const finite = z.number().finite();
const positive = z.number().finite().positive();
const shortLabel = z.string().trim().min(1).max(60);
/**
 * `alt` is mandatory on every variant: it is the accessible name, the print
 * caption, and the fallback shown when a render times out. A figure with no
 * usable description is not shippable, so the schema refuses one.
 */
const altText = z
    .string()
    .trim()
    .min(8, 'alt must describe the figure in at least 8 characters')
    .max(400);
const caption = z.string().trim().max(200);
const seriesStyle = z.enum(['solid', 'dashed', 'dotted']);
const coordinate = z.tuple([finite, finite]);
const plotSeries = z
    .object({
    label: shortLabel,
    style: seriesStyle.optional(),
    /** Expression in `x`, evaluated by our own parser — never by `eval`. */
    expr: z
        .string()
        .trim()
        .min(1)
        .max(FIGURE_LIMITS.maxExpressionLength)
        .refine(isSupportedExpression, {
        message: 'expression uses tokens outside the allow-list',
    })
        .optional(),
    /** Pre-sampled `[x, y]` pairs, for data that is not a function of x. */
    points: z.array(coordinate).min(2).max(FIGURE_LIMITS.maxSeriesPoints).optional(),
    /** Restrict where `expr` is sampled; defaults to the plot's x range. */
    domain: z.tuple([finite, finite]).optional(),
})
    .refine((series) => Boolean(series.expr) !== Boolean(series.points), {
    message: 'series needs exactly one of expr or points',
})
    .refine((series) => !series.domain || series.domain[0] < series.domain[1], {
    message: 'series domain must be increasing',
});
const plotMarker = z.object({
    x: finite,
    y: finite,
    label: shortLabel.optional(),
    /** Hollow marker — an excluded value, matching number-line convention. */
    open: z.boolean().optional(),
});
const cartesianPlot = z.object({
    kind: z.literal('cartesian_plot'),
    alt: altText,
    caption: caption.optional(),
    xMin: finite,
    xMax: finite,
    yMin: finite,
    yMax: finite,
    xLabel: shortLabel.optional(),
    yLabel: shortLabel.optional(),
    xStep: positive.optional(),
    yStep: positive.optional(),
    grid: z.boolean().optional(),
    series: z.array(plotSeries).max(FIGURE_LIMITS.maxSeries).optional(),
    points: z.array(plotMarker).max(FIGURE_LIMITS.maxMarkers).optional(),
});
const numberLinePoint = z.object({
    value: finite,
    label: shortLabel.optional(),
    /** Hollow circle = value excluded; solid = included. */
    open: z.boolean().optional(),
});
const numberLineInterval = z.object({
    /** null (or omitted) means unbounded — drawn as an arrow. */
    from: finite.nullable().optional(),
    to: finite.nullable().optional(),
    fromClosed: z.boolean().optional(),
    toClosed: z.boolean().optional(),
    label: shortLabel.optional(),
});
const numberLine = z.object({
    kind: z.literal('number_line'),
    alt: altText,
    caption: caption.optional(),
    min: finite,
    max: finite,
    /** Major tick spacing. Derived from the range when omitted. */
    step: positive.optional(),
    /** Optional unlabelled subdivisions between major ticks. */
    minorStep: positive.optional(),
    label: shortLabel.optional(),
    points: z.array(numberLinePoint).max(FIGURE_LIMITS.maxMarkers).optional(),
    intervals: z.array(numberLineInterval).max(FIGURE_LIMITS.maxIntervals).optional(),
});
/**
 * Discriminated union first, cross-field rules second: `z.discriminatedUnion`
 * only accepts plain objects, so the range checks have to live on the outside.
 */
const figureUnion = z.discriminatedUnion('kind', [cartesianPlot, numberLine]);
function tooManyTicks(span, step) {
    if (!step)
        return false;
    return span / step > FIGURE_LIMITS.maxTicksPerAxis;
}
export const FigureSpecSchema = figureUnion.superRefine((spec, ctx) => {
    if (spec.kind === 'cartesian_plot') {
        if (spec.xMin >= spec.xMax) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['xMax'], message: 'xMax must exceed xMin' });
        }
        if (spec.yMin >= spec.yMax) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['yMax'], message: 'yMax must exceed yMin' });
        }
        if (tooManyTicks(spec.xMax - spec.xMin, spec.xStep)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['xStep'], message: 'xStep produces too many ticks' });
        }
        if (tooManyTicks(spec.yMax - spec.yMin, spec.yStep)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['yStep'], message: 'yStep produces too many ticks' });
        }
        return;
    }
    if (spec.min >= spec.max) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['max'], message: 'max must exceed min' });
    }
    if (tooManyTicks(spec.max - spec.min, spec.step)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['step'], message: 'step produces too many ticks' });
    }
    if (spec.minorStep && spec.step && spec.minorStep >= spec.step) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['minorStep'],
            message: 'minorStep must be smaller than step',
        });
    }
    (spec.intervals || []).forEach((interval, index) => {
        const from = interval.from ?? null;
        const to = interval.to ?? null;
        if (from === null && to === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['intervals', index],
                message: 'interval must be bounded on at least one side',
            });
        }
        if (from !== null && to !== null && from >= to) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['intervals', index, 'to'],
                message: 'interval `to` must exceed `from`',
            });
        }
    });
});
/**
 * Optional figure field for question schemas. Kept separate so callers can
 * splice it into their own object schema without importing zod internals.
 */
export const OptionalFigureSchema = FigureSpecSchema.optional();
const MAX_DECODE_DEPTH = 8;
/**
 * Deep-decode API-sanitiser entities in a figure-shaped value.
 *
 * The API HTML-encodes every string it stores, so a saved `expr` comes back as
 * `1&#x2F;x` — not a formula the evaluator recognises. Decoding here means a
 * figure renders the same whether it arrived straight from the generator or
 * from a round-trip through the database.
 * `server/validators/figure_spec.js` carries the same decode.
 */
export function decodeFigureStrings(value, depth = 0) {
    if (typeof value === 'string')
        return decodeQuizHtmlEntities(value);
    if (depth >= MAX_DECODE_DEPTH || !value || typeof value !== 'object')
        return value;
    if (Array.isArray(value))
        return value.map((item) => decodeFigureStrings(item, depth + 1));
    const out = {};
    for (const [key, item] of Object.entries(value)) {
        out[key] = decodeFigureStrings(item, depth + 1);
    }
    return out;
}
/** Convenience wrapper for call sites that only care about pass/fail. */
export function parseFigureSpec(value) {
    const result = FigureSpecSchema.safeParse(decodeFigureStrings(value));
    if (result.success)
        return { ok: true, spec: result.data };
    const first = result.error.issues[0];
    const where = first?.path?.length ? `${first.path.join('.')}: ` : '';
    return { ok: false, error: `${where}${first?.message || 'invalid figure spec'}` };
}
/** True when `value` is a figure we can actually draw today. */
export function isFigureSpec(value) {
    return parseFigureSpec(value).ok;
}
